import React, { useEffect, useState, useCallback, useMemo } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import SearchBox from '@components/common/search-box';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@i18n/client';
import useQueryParam from '@utils/use-query-params';

type Props = {
  lang: string;
  className?: string;
  searchId?: string;
  variant?: 'border' | 'fill';
};

const ProductsGridSearch = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className = 'md:w-[730px] 2xl:w-[800px]',
      searchId = 'products-grid-search',
      variant = 'border',
      lang,
    },
    ref,
  ) => {
    const { t } = useTranslation(lang);
    const location = useLocation();
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const { updateQueryParams, clearQueryParam } = useQueryParam(
      pathname ?? '/',
    );
    const [searchText, setSearchText] = useState('');

    const hasQueryKey = searchParams?.get('q');

    const updateQuery = useCallback(
      (value: string) => {
        if (value.length === 0) {
          clearQueryParam(['q']);
        } else {
          updateQueryParams('q', value);
        }
      },
      [updateQueryParams, clearQueryParam],
    );

    // Debounced function to update query parameters
    const debouncedUpdateQuery = useMemo(
      () =>
        debounce((value: string) => {
          updateQuery(value);
        }, 500),
      [updateQuery],
    );

    useEffect(() => {
      debouncedUpdateQuery(searchText);

      // Cleanup function to cancel pending debounced calls
      return () => {
        debouncedUpdateQuery.cancel();
      };
    }, [searchText, debouncedUpdateQuery]);

    useEffect(() => {
      if (hasQueryKey) {
        setSearchText(hasQueryKey);
      } else {
        setSearchText('');
      }
    }, [hasQueryKey]);

    function handleSearch(e: React.SyntheticEvent) {
      e.preventDefault();
      // Do nothing - let the ProductsGrid handle the results
    }

    function handleInputChange(e: React.FormEvent<HTMLInputElement>) {
      setSearchText(e.currentTarget.value);
    }

    function clear() {
      setSearchText('');
    }

    return (
      <div
        ref={ref}
        className={cn(
          'w-full transition-all duration-200 ease-in-out',
          className,
        )}
      >
        <div className="relative z-30 flex flex-col justify-center w-full shrink-0">
          <div className="flex flex-col w-full mx-auto">
            <SearchBox
              searchId={searchId}
              name="search"
              value={searchText}
              onSubmit={handleSearch}
              onChange={handleInputChange}
              onClear={clear}
              variant={variant}
              lang={lang}
            />
          </div>
        </div>
      </div>
    );
  },
);

ProductsGridSearch.displayName = 'ProductsGridSearch';
export default ProductsGridSearch;
